import JSZip from "jszip";

import { apiGet } from "../../services/apiService";
import { AdditionalMeterDetail, MeterDetails } from "../../types/components.types";
import { csvDownloadResponse, MultiMpanResult } from "../../types/services.types";

import { getHalfHourlyResults } from "./electralink.utils";

const endpoints = {
  halfhourly_csv: "/electralink/halfhourly/forcsv",
};

// The data from the middleware is returned in nested objects.
// We need to unnest these objects in order to be able to export the entire data to CSV.
// TS type is any for now.
// Prefix is a string that will hold the path of the keys through nested objects, so we know where each value came from.
const unnestedData = (object: Record<string, unknown>, prefix = ""): Record<string, string> => {
  // Create an empty object to store the final 'flattened' result. Each key is the path to the original nested value.
  const result: Record<string, string> = {};

  // Loop through every key in a given object (from the API response).
  for (const key in object) {
    if (Object.prototype.hasOwnProperty.call(object, key)) {
      // If a prefix exists, build a new key name by combining the prefix and the current key, separated by an underscore e.g.,
      // prefix = 'payload_additional_detail_0' key = 'additional_detail_msn' will result in: prefixedKey = "payload_additional_detail_0_additional_detail_msn";
      // The key will reflect the full path in the original nested object structure, so we know where each value came from.
      const prefixedKey = prefix ? `${prefix}_${key}` : key;

      // Get the value from the object using the current key.
      const value = object[key];

      // If a a value is an object/array and not null, you need to flatten it too. This is useful for objects/arrays like: additional_detail and tpr_data from the API response.
      if (typeof value === "object" && value !== null) {
        // Call the unnestedData again and pass it the nested object/array (value) and its path in 'prefixedKey' to flatten it too.
        const nestedResult = unnestedData(value as Record<string, unknown>, prefixedKey);

        // Merge each key-value pair from the nested result into the main result, so you can accumulate all the flattened values into one single object.
        for (const nestedKey in nestedResult) {
          if (Object.prototype.hasOwnProperty.call(nestedResult, nestedKey)) {
            result[nestedKey] = nestedResult[nestedKey];
          }
        }
      } else {
        // If the value is not an object (e.g., string/number), add it to the main result using the prefixed key to preserve its original path.
        result[prefixedKey] = String(value);
      }
    }
  }
  return result;
};

export const triggerCsvDownload = (blob: Blob, filename: string): void => {
  // Create a downloadable URL for the blob object.
  const url = URL.createObjectURL(blob);

  // Create an anchor element (<a>) to serve as a link and initiate the CSV download once triggered.
  const link = document.createElement("a");

  // Set the href attribute to the generated URL. The <a> tag will now point to the file, making it downloadable.
  link.href = url;

  link.download = filename;

  // Append the link to the document body temporarily.
  // This is because in some old browsers, the element must be in the DOM for the click event to work.
  document.body.appendChild(link);

  // Trigger the download automatically when a user clicks on "Download CSV".
  link.click();

  // Remove the link from the DOM after the click. It prevents unused elements in the DOM.
  document.body.removeChild(link);

  // Revoke the temporary Blob URL to free up memory.
  URL.revokeObjectURL(url);
};

export const downloadMeterDetailsCSV = (meterDetails: MeterDetails): csvDownloadResponse => {
  // Get the complete data object for the current meter.
  const dataToExport = meterDetails;

  try {
    const unnestedMeterDetails = Object.fromEntries(
      // Filter out empty/null/undefined values from the unnested data.
      Object.entries(unnestedData(dataToExport as unknown as Record<string, unknown>)).filter(
        ([_, value]) => value !== "",
      ),
    );

    // Extract all the keys from the unnested data and join them with commas.
    // The keys will be the headers in the CSV file to provide column names.
    const headers = Object.keys(unnestedMeterDetails).join(",");

    // Extract all the values from the unnested object and format them to strings.
    // This will ensure that values that start with 0 will not be removed when exported to CSV as those values are now strings not 'numbers'.
    const values = Object.values(unnestedMeterDetails)

      // The (\t) forces excel to treat all values as strings.
      .map((value) => `\t"${value}"`)

      // Join all values with commas to form a single CSV row.
      .join(",");

    // Structure the headers and values in a CSV format.
    const csvData = `${headers}\n${values}`;

    // Create a blob object containing the CSV data. Blob generates a file and provide a download link for the user without interacting with the user's server.
    const blob = new Blob([csvData], { type: "text/csv" });

    const filename = `meter_details_${meterDetails.mpan}.csv`;
    triggerCsvDownload(blob, filename);
    console.log("CSV downloaded successfully");
    return { success: true };
  } catch (error) {
    console.log("Error downloading CSV:", error);
    return { success: false, error: "Failed to download meter details CSV" };
  }
};

export const downloadHalfHourlyCsv = async (mpan: string): Promise<csvDownloadResponse> => {
  try {
    const response = await apiGet<string>(`${endpoints.halfhourly_csv}/${mpan}`, {
      responseType: "text",
    });
    const blob = new Blob([response], { type: "text/csv" });

    // Build a descriptive filename that includes the MPAN to help the user identify which MPAN this data belongs to.
    const filename = `halfhourly_calculations_${mpan}.csv`;

    // Trigger the browser download using the function built above to avoid code duplication.
    triggerCsvDownload(blob, filename);
    console.log("Half-hourly CSV downloaded successfully.");
    return { success: true };
  } catch (error) {
    console.log("Error downloading half-hourly CSV:", error);
    return { success: false, error: "Failed to download half-hourly CSV." };
  }
};

// One function to handle different types of meter CSV exports (HH / non HH), instead of writing multiple functions serving similar purpose.
export const downloadCSV = async (meterDetails: MeterDetails): Promise<csvDownloadResponse> => {
  // Check if the meter is half-hourly.
  if (meterDetails.is_half_hourly) {
    console.log("Downloading half-hourly TAQ calculations to CSV.");
    return await downloadHalfHourlyCsv(meterDetails.mpan);
  } else {
    console.log("Exporting meter details to CSV.");
    return downloadMeterDetailsCSV(meterDetails);
  }
};

// Helper function to escape CSV values that contain commas, quotes or new lines.
// The API returns null for some fields its types declare as strings, so null becomes an empty cell.
const escapeCSVValue = (value: string | null | undefined): string => {
  if (value === null || value === undefined) return "";
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

// Format a date string to DD/MM/YYYY to match Joe's Zoho CSV format.
// Accepts ISO format (YYYY-MM-DD) or any value parseable by Date.
// Returns the original value unchanged if it cannot be parsed as a valid date.
const formatDate = (value: string | null | undefined): string => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  const dd = String(date.getUTCDate()).padStart(2, "0");
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = date.getUTCFullYear();
  return `${dd}/${mm}/${String(yyyy)}`;
};

// Build a single combined address string from the metering point address lines and post code.
// Empty/null/undefined parts are filtered out before joining to avoid stray commas.
const buildAddress = (details: MeterDetails): string => {
  const parts = [
    details.metering_point_address_line_1,
    details.metering_point_address_line_2,
    details.metering_point_address_line_3,
    details.metering_point_address_line_4,
    details.metering_point_address_line_5,
    details.metering_point_address_line_6,
    details.metering_point_address_line_7,
    details.metering_point_address_line_8,
    details.metering_point_address_line_9,
    details.post_code,
  ];
  return parts.filter((p) => p !== "").join(", ");
};

const padAndSlice = (
  value: string | null | undefined,
  padBy: number,
  sliceBy: number,
  padWith = "0",
  sliceFromRight = true,
) => {
  if (value === null || value === undefined || value === "") return "";

  const padded = value.padStart(padBy, padWith);
  return sliceFromRight ? padded.slice(-sliceBy) : padded.slice(0, sliceBy);
};

// Build the MPAN top line in Joe's format: apostrophe-prefixed profile class + SSC + line loss factor class ID.
// The leading apostrophe forces Excel to treat the value as text, preserving any leading zeros.
// Returns an empty string if any component is missing.
const buildMpanTopLine = (details: MeterDetails): string => {
  const profileClass = details.additional_detail[0]?.additional_detail_profile_class;
  const ssc = details.standard_settlement_configuration_id;
  const llfc = details.line_loss_factor_class_id;
  if (!profileClass || !ssc || !llfc) return "";
  // Use the last 3 characters of the LLFC to match Joe's Zoho implementation (llfc.right(3)).

  const profileClassPadded = padAndSlice(profileClass, 2, 2);
  const sscPadded = padAndSlice(ssc, 4, 4);
  const llfcPadded = padAndSlice(llfc, 3, 3);

  return `'${profileClassPadded}${sscPadded}${llfcPadded}`;
};

// Calculate the total AQ (Annual Quantity) for a meter.
// For non-HH meters: sum all tpr_eac values across all TPR records.
// For HH meters: use the TAQ value from hh_aq_calc.
const calculateAQ = (details: MeterDetails): string => {
  if (details.is_half_hourly) {
    return details.hh_aq_calc?.taq.toString() ?? "";
  }
  if (details.tpr_data.length === 0) return "0";
  const total = details.tpr_data.reduce((sum, tpr) => sum + (tpr.tpr_eac || 0), 0);
  return total.toString();
};

// Download multiple MPANs to a single CSV, mirroring Joe's Zoho format.
// One row per MPAN, 27 columns matching Joe's column names and order.
export const downloadMultiMpanCSV = (results: MultiMpanResult[]): void => {
  const headers = [
    "MPAN",
    "AQ",
    "Mpan Top Line",
    "TPR",
    "EAC Effective From",
    "EAC Count",
    "Address",
    "Supplier ID",
    "Supplier Name",
    "Supplier EFD",
    "ET",
    "Line Loss Factor Class",
    "LLFC Effective From",
    "Energisation Status",
    "ES Effective From",
    "Standard Settlement Configuration",
    "SSC Effective From",
    "MSN",
    "Meter Type",
    "Install Date",
    "Model",
    "SMSO ID",
    "DNO ID",
    "Profile Class",
    "MTC ID",
    "GSP ID",
    "MSN Count",
  ];

  // Build one row per MPAN result.
  const rows = results.map((result) => {
    // For failed results, return the MPAN with empty fields so it still appears in the CSV.
    if (!result.success || !result.meterDetails) {
      return [`'${result.mpan}`, ...Array<string>(headers.length - 1).fill("")]
        .map(escapeCSVValue)
        .join(",");
    }

    const meterDetails = result.meterDetails;
    const additionalDetail = meterDetails.additional_detail[0] as AdditionalMeterDetail | undefined;

    // TPR count: number of TPR records on the meter.
    const tprCount = meterDetails.tpr_data.length;

    // EAC Effective From: use the most recent eac_efd across all TPR records.
    const eacEfd = meterDetails.tpr_data.length
      ? meterDetails.tpr_data[meterDetails.tpr_data.length - 1].eac_efd
      : "";

    // EAC Count: number of TPR records (matches Joe's 'EAC Count' column).
    const eacCount = tprCount.toString();

    const rowValues = [
      `'${result.mpan}`,
      calculateAQ(meterDetails),
      buildMpanTopLine(meterDetails),
      tprCount.toString(),
      // Format EAC Effective From date to DD/MM/YYYY to match Joe's format.
      formatDate(eacEfd),
      eacCount,
      buildAddress(meterDetails),
      meterDetails.supplier_id,
      meterDetails.supplier_name,
      // Format all date fields to DD/MM/YYYY to match Joe's format.
      formatDate(meterDetails.supplier_efd),
      meterDetails.et,
      padAndSlice(meterDetails.line_loss_factor_class_id, 3, 3),
      formatDate(meterDetails.line_loss_factor_class_id_effective_from_date),
      meterDetails.energisation_status,
      formatDate(meterDetails.energisation_status_effective_from_date),
      meterDetails.standard_settlement_configuration_id,
      formatDate(meterDetails.standard_settlement_configuration_id_effective_from_date),
      additionalDetail?.additional_detail_msn ?? "",
      additionalDetail?.additional_detail_meter_type ?? "",
      formatDate(additionalDetail?.additional_detail_make_install_date),
      additionalDetail?.additional_detail_make_model ?? "",
      // Output 'null' as a literal string when SMSO ID is absent, matching Joe's format.
      additionalDetail?.additional_detail_smso_id ?? "null",
      additionalDetail?.additional_detail_dno_id ?? "",
      padAndSlice(additionalDetail?.additional_detail_profile_class, 2, 2),
      padAndSlice(additionalDetail?.additional_detail_mtc_id, 3, 3),
      additionalDetail?.additional_detail_gsp_group_id ?? "",
      // MSN Count: always 1 per meter record to match Joe's format.
      "1",
    ];

    return rowValues.map(escapeCSVValue).join(",");
  });

  // Combine headers and rows into final CSV content.
  const csvContent = [headers.map(escapeCSVValue).join(","), ...rows].join("\n");

  // Create blob and trigger download.
  const blob = new Blob([csvContent], { type: "text/csv" });
  const timestamp = new Date().toISOString().split("T")[0];
  const filename = `multi_mpan_search_${timestamp}.csv`;

  triggerCsvDownload(blob, filename);
};

// Download HH calculations: single CSV for 1 HH meter and a ZIP file for multiple HH meters.
export const downloadHHCalculationsExport = async (
  results: MultiMpanResult[],
): Promise<csvDownloadResponse> => {
  // Filter out only the successful HH meters.
  const hhResults = getHalfHourlyResults(results);

  if (hhResults.length === 0) {
    return { success: false, error: "No half-hourly meters found" };
  }

  // For single HH meter use the existing function.
  if (hhResults.length === 1) {
    const mpan = hhResults[0].mpan;
    return await downloadHalfHourlyCsv(mpan);
  }

  // For multiple HH meters fetch all and bundle into a ZIP file
  try {
    const zip = new JSZip();

    // Fetch CSV data for each HH meter in parallel.
    const fetchPromises = hhResults.map(async (result) => {
      const mpan = result.mpan;
      try {
        const response = await apiGet<string>(`${endpoints.halfhourly_csv}/${mpan}`, {
          responseType: "text",
        });
        return { mpan, data: response, success: true };
      } catch (error) {
        console.log(`Failed to fetch HH data for ${mpan}:`, error);
        return { mpan, data: null, success: false };
      }
    });

    const fetchResults = await Promise.all(fetchPromises);

    // Add the successful HH meters to a ZIP file.
    let addedCount = 0;
    fetchResults.forEach(({ mpan, data, success }) => {
      if (success && data) {
        zip.file(`halfhourly_calculations_${mpan}.csv`, data);
        addedCount++;
      }
    });

    if (addedCount === 0) {
      return { success: false, error: "Failed to fetch any HH calculation data" };
    }

    // Generate and download the ZIP file.
    const zipBlob = await zip.generateAsync({ type: "blob" });
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `hh_calculations_${timestamp}.zip`;

    triggerCsvDownload(zipBlob, filename);
    console.log(`HH calculations ZIP downloaded with ${String(addedCount)} files`);

    return { success: true };
  } catch (error) {
    console.log("Error creating HH calculations ZIP:", error);
    return { success: false, error: "Failed to create HH calculations export" };
  }
};
